import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner } from './Spinner';
import { acquireTokenSilent } from '../config/msalConfig';
import { ApiService } from '../services/apiService';
import FeatherIcon from 'feather-icons-react';

export interface AttachmentLike {
  id: string;
  filename: string;
  size: number;
  type?: string;
  uploadedAt?: string | null;
  mime_type?: string;
  storage_key?: string;
  downloadUrl?: string;
  delivery_type?: string;
}

interface PDFPreviewPanelProps {
  attachments: AttachmentLike[];
  referralId: string;
  isLoading?: boolean;
  userRole?: string;
  isSrReferral?: boolean;
  focusAttachmentId?: string | null;
  onAttachmentUploaded?: (attachment: AttachmentLike) => void;
  onUploadComplete?: () => void;
}

// AXIS212: Allowed file types for upload
const ALLOWED_FILE_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.doc', '.jpeg', '.jpg', '.png'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// AXIS216: Helper function to identify email body PDFs by filename pattern
const isEmailBodyPdf = (filename: string): boolean => {
  const pattern = /^email-body(-[a-f0-9]{8})?\.pdf$/i;
  return pattern.test(filename);
};

function formatPreviewFileSize(bytes?: number | null): string {
  if (bytes == null || bytes <= 0) {
    return '—';
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export const PDFPreviewPanel: React.FC<PDFPreviewPanelProps> = ({
  attachments,
  referralId,
  isLoading = false,
  userRole,
  isSrReferral = false,
  focusAttachmentId = null,
  onAttachmentUploaded,
  onUploadComplete
}) => {
  // Filter to only PDF attachments, excluding inline images
  const pdfAttachments = attachments.filter(att => {
    // Skip inline images (used for email body PDF generation, not for user viewing)
    const filename = att.filename?.toLowerCase() || '';
    if (filename.startsWith('inline_')) {
      return false;
    }

    // Filter to only PDFs
    const isPdfByType = att.type && att.type.toLowerCase() === 'pdf';
    const isPdfByMimeType = att.mime_type && att.mime_type.toLowerCase() === 'application/pdf';
    const isPdfByFilename = filename.endsWith('.pdf');
    return isPdfByType || isPdfByMimeType || isPdfByFilename;
  });

  const [selectedAttachmentId, setSelectedAttachmentId] = useState<string | null>(
    pdfAttachments && pdfAttachments.length > 0 ? pdfAttachments[0].id : null
  );
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setError] = useState<string | null>(null);

  // AXIS212: Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // AXIS212: All authenticated users can upload attachments (email referrals only)
  const canUpload = !!userRole && !isSrReferral;

  // AXIS216: Auto-select email body PDF if available, otherwise select first PDF
  useEffect(() => {
    if (pdfAttachments.length > 0) {
      if (focusAttachmentId && pdfAttachments.some((att) => att.id === focusAttachmentId)) {
        setSelectedAttachmentId(focusAttachmentId);
        return;
      }

      const emailBodyPdf = isSrReferral
        ? null
        : pdfAttachments.find(att => isEmailBodyPdf(att.filename));

      const originalSrPdf = isSrReferral
        ? pdfAttachments.find((att) => att.delivery_type === 'original')
        : null;
      
      const selectedIsPdf = pdfAttachments.some(att => att.id === selectedAttachmentId);
      if (!selectedAttachmentId || !selectedIsPdf) {
        const defaultAttachment = emailBodyPdf || originalSrPdf || pdfAttachments[0];
        setSelectedAttachmentId(defaultAttachment.id);
      }
    } else {
      setSelectedAttachmentId(null);
    }
  }, [pdfAttachments, selectedAttachmentId, isSrReferral, focusAttachmentId]);

  // Fetch PDF URL when attachment changes
  useEffect(() => {
    if (!selectedAttachmentId) {
      setPdfUrl(null);
      return;
    }

    const fetchPdfUrl = async () => {
      setPdfLoading(true);
      setError(null);
      try {
        // Acquire Entra ID access token via MSAL
        let token: string | null = null;
        try {
          const tokenResult = await acquireTokenSilent();
          token = tokenResult.accessToken;
        } catch (tokenError) {
          console.warn('Failed to acquire access token:', tokenError);
        }

        // Call the backend endpoint to get the authenticated SAS URL
        const baseUrl = (import.meta.env.VITE_FUNCTION_APP_URL || 'https://func-axis-referral-dev.azurewebsites.net') + '/api';
        const endpoint = `${baseUrl}/referrals/${referralId}/attachments/${selectedAttachmentId}`;

        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to get attachment: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (!data.download_url) {
          throw new Error('No download URL in response');
        }

        // Use the SAS URL from the response (it has embedded authentication)
        setPdfUrl(data.download_url);
      } catch (err) {
        console.error('Failed to load PDF preview:', err);
        setError(`Failed to load PDF: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setPdfLoading(false);
      }
    };

    fetchPdfUrl();
  }, [selectedAttachmentId, referralId]);

  const selectedAttachment = pdfAttachments.find(att => att.id === selectedAttachmentId);
  const showLargeFileWarning = selectedAttachment && selectedAttachment.size > 5 * 1024 * 1024;

  // AXIS212: Handle file selection
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset state
    setUploadError(null);
    setUploadProgress(0);

    // Validate file type
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExt)) {
      const allowedTypes = ALLOWED_EXTENSIONS.join(', ');
      setUploadError(`Invalid file type. Allowed types: ${allowedTypes}`);
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`File too large. Maximum size: ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      return;
    }

    // Start upload
    setUploading(true);
    try {
      const result = await ApiService.uploadAttachment(
        referralId,
        file,
        (progress) => setUploadProgress(progress)
      );

      // Create new attachment object from response
      const newAttachment: AttachmentLike = {
        id: result.attachment_id,
        filename: result.filename,
        size: result.file_size,
        mime_type: result.content_type,
        storage_key: result.blob_path,
        uploadedAt: result.uploaded_at
      };

      // Notify parent component
      if (onAttachmentUploaded) {
        onAttachmentUploaded(newAttachment);
      }

      // Auto-select the newly uploaded attachment if it's a PDF
      if (result.content_type === 'application/pdf' || result.filename.toLowerCase().endsWith('.pdf')) {
        setSelectedAttachmentId(result.attachment_id);
      }

      if (onUploadComplete) {
        onUploadComplete();
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [referralId, onAttachmentUploaded, onUploadComplete]);

  // AXIS212: Trigger file input click
  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="pdf-preview-panel">
      <div className="pdf-preview-header">
        <h5>Document Preview</h5>
        <div className="pdf-header-controls">
          {pdfAttachments.length > 1 && (
            <div className="attachment-selector">
              <label htmlFor="pdf-select">Select Document:</label>
              <div className="attachment-selector-row" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <select
                  id="pdf-select"
                  value={selectedAttachmentId || ''}
                  onChange={(e) => {
                    setSelectedAttachmentId(e.target.value);
                  }}
                  className="form-control"
                  style={{ flex: '1', minWidth: '200px' }}
                >
                  {pdfAttachments.map(att => (
                    <option key={att.id} value={att.id}>
                      {att.filename}
                    </option>
                  ))}
                </select>

                {/* AXIS212: Upload Attachment button - visible to all authenticated users */}
                {canUpload && (
                  <div className="upload-control">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept={ALLOWED_FILE_TYPES.join(',')}
                      style={{ display: 'none' }}
                      disabled={uploading}
                    />
                    <button
                      className="btn btn-sm btn-secondary upload-btn"
                      onClick={handleUploadClick}
                      disabled={uploading}
                      title="Upload Attachment"
                    >
                      {uploading ? (
                        <>
                          <Spinner size="sm" color="default" />
                          <span className="upload-text">{uploadProgress}%</span>
                        </>
                      ) : (
                        <>
                          <FeatherIcon icon="upload" size={16} />
                          <span className="upload-text">Upload</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AXIS212: Upload button when there's only 1 attachment */}
          {pdfAttachments.length <= 1 && canUpload && (
            <div className="upload-control">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept={ALLOWED_FILE_TYPES.join(',')}
                style={{ display: 'none' }}
                disabled={uploading}
              />
              <button
                className="btn btn-sm btn-secondary upload-btn"
                onClick={handleUploadClick}
                disabled={uploading}
                title="Upload Attachment"
              >
                {uploading ? (
                  <>
                    <Spinner size="sm" color="default" />
                    <span className="upload-text">{uploadProgress}%</span>
                  </>
                ) : (
                  <>
                    <FeatherIcon icon="upload" size={16} />
                    <span className="upload-text">Upload</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* AXIS212: Upload error message */}
      {uploadError && (
        <div className="upload-error-banner">
          <FeatherIcon icon="alert-circle" size={16} />
          <span>{uploadError}</span>
          <button
            className="error-dismiss"
            onClick={() => setUploadError(null)}
            title="Dismiss"
          >
            <FeatherIcon icon="x" size={14} />
          </button>
        </div>
      )}

      {selectedAttachment && (
        <div className="pdf-preview-info">
          <span className="pdf-filename">{selectedAttachment.filename}</span>
          <span className="pdf-filesize">
            {formatPreviewFileSize(selectedAttachment.size)}
          </span>
        </div>
      )}

      <div className="pdf-preview-container">
        {isLoading || pdfLoading ? (
          <div className="pdf-loading">
            {showLargeFileWarning && <p className="loading-info">Loading large file...</p>}
            <Spinner size="lg" color="default" />
          </div>
        ) : pdfError ? (
          <div className="pdf-error">
            <p>⚠️ {pdfError}</p>
            <p className="error-detail">Unable to display PDF preview at this time.</p>
          </div>
        ) : pdfUrl && selectedAttachment ? (
          <div className="pdf-viewer-wrapper">
            <iframe
              src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              title={`Preview: ${selectedAttachment.filename}`}
              className="pdf-iframe"
              onLoad={() => {
                // PDF loaded successfully
                setPdfLoading(false);
              }}
              onError={() => {
                setError('Unable to display PDF in browser');
                setPdfLoading(false);
              }}
            />
            <div className="pdf-controls">
              <span className="page-info">
                Viewing PDF ({formatPreviewFileSize(selectedAttachment.size)})
              </span>
            </div>
          </div>
        ) : (
          <div className="pdf-empty">
            <p>No PDF documents available to preview</p>
            {isSrReferral && (
              <p className="notice-detail">Referral PDF will appear after ingest</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
